/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import type { ReactChild } from 'react';

import { simplifyQueryValue } from 'js/lib/queryUtils';
import { useLocation, useNavigate } from 'js/lib/useRouter';

import {
  Button,
  Dialog,
  InlineNotification,
  Link,
  PasswordField,
  TextField,
  Typography2,
  VisuallyHidden,
} from '@coursera/cds-core';
import type { EOIButtonSource } from '@coursera/event-pulse-types';

import Separator from 'bundles/authentication-common/components/Separator';
import * as actions from 'bundles/authentication-common/components/actions';
import {
  ARKOSE_LOGIN_KEY,
  LOGIN_REDESIGN_RECAPTCHA_ID,
  RECAPTCHA_ACTIONS,
} from 'bundles/authentication-common/constants/botManager';
import {
  EMAIL_CONSTRAINT as isEmailValid,
  PASSWORD_CONSTRAINT as isPasswordValid,
} from 'bundles/authentication-common/constants/constraints';
import useBotManager from 'bundles/authentication-common/hooks/useBotManager';
import { HANDLED_ERRORS, NEEDS_2FA_ERRORS, errorMessageFactory } from 'bundles/authentication-common/utils/loginUtils';
import EoiSSO from 'bundles/expression-of-interest/components/authentication/EoiSSO';
import InternationalPhoneNumberInput from 'bundles/expression-of-interest/components/i18n-phone-number-input/InternationalPhoneNumberInput';
import type { InternationalPhoneNumber } from 'bundles/expression-of-interest/components/i18n-phone-number-input/types';
import {
  DEFAULT_REGION,
  newCountryCode,
} from 'bundles/expression-of-interest/components/i18n-phone-number-input/types';
import {
  isValidInternationalPhoneNumber,
  toRegionFromCountryCode,
} from 'bundles/expression-of-interest/components/i18n-phone-number-input/utils';
import { ACTION_QUERY_PARAMS, CONTINUE_EOI_PARAMS, SECTION_EVENT_MAP } from 'bundles/expression-of-interest/constants';
import { useAriaAnnouncement } from 'bundles/expression-of-interest/hooks/useAriaAnnouncement';
import type { ActionQueryParam } from 'bundles/expression-of-interest/types';
import type { ProfilePhone } from 'bundles/expression-of-interest/types';
import { useEoiEventing } from 'bundles/expression-of-interest/utils/eventingUtils';
import { getDefaultError, setActionQueryParamAndReload } from 'bundles/expression-of-interest/utils/util';
import type { Phone } from 'bundles/naptimejs/resources/__generated__/ProfilesV1';
import { customFormattedPhoneStyles } from 'bundles/university-program-qualification/components/SurveyQuestion';
import { trackAuthAttempts } from 'bundles/user-account/utils/authTrackingUtils';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

import _t from 'i18n!nls/expression-of-interest';

/** Delay for focus restoration after error announcement to allow screen reader time to read */
const FOCUS_DELAY_AFTER_ANNOUNCEMENT_MS = 200;

type Props = {
  learnerEmail: string;
  phone: Phone;
  handleEmailChange: (email: string) => void;
  handlePhoneChange: (phone: ProfilePhone) => void;
  handlePreviousSection: () => void;
  handleSubmit: () => Promise<void | {}>;
  trackingProductId: string;
  shouldSetAutoFocus?: boolean;
  navigateConfirmation?: () => void;
  eoiButtonSource?: EOIButtonSource;
};

const styles = {
  primaryButton: css`
    margin-right: var(--cds-spacing-200);
  `,
  reCAPTCHA: css`
    display: none;
  `,
  passwordField: css`
    margin-top: var(--cds-spacing-150);
    margin-bottom: var(--cds-spacing-150);
  `,
  supportText: css`
    margin-top: var(--cds-spacing-150);
  `,
};

export const LoginSection = (props: Props): React.ReactElement => {
  const {
    learnerEmail,
    phone,
    handleEmailChange,
    handlePreviousSection,
    handlePhoneChange,
    handleSubmit,
    trackingProductId,
    shouldSetAutoFocus,
    navigateConfirmation,
    eoiButtonSource,
  } = props;
  const { trackInteractEoi, trackSubmitEoi } = useEoiEventing(trackingProductId);
  const location = useLocation();
  const navigate = useNavigate();

  const phoneNumberRef = useRef<HTMLInputElement>(null);
  const verificationCodeInput = useRef<HTMLInputElement | null>(null);
  const [password, setPassword] = useState<string>('');
  const [hasFormError, setHasFormError] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [hasPhoneError, setHasPhoneError] = useState<boolean>(false);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [has2FA, setHas2FA] = useState<boolean>(false);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [formError, setFormError] = useState<string | ReactChild>('');
  const { announcement: ariaAnnouncement, announce } = useAriaAnnouncement();

  const {
    isRetrievingTokenRef,
    getTokenData: getBotManagerTokenData,
    reset: resetBotManager,
  } = useBotManager({
    arkoseBotManager: {
      publicKey: ARKOSE_LOGIN_KEY,
    },
    reCAPTCHA: {
      container: LOGIN_REDESIGN_RECAPTCHA_ID,
      options: { action: RECAPTCHA_ACTIONS.login },
    },
  });

  const disclaimerText = _t(
    'By sharing my phone number, I consent to receiving calls or texts from Coursera about educational opportunities.'
  );

  const baseTrackingData = {
    eoiSection: SECTION_EVENT_MAP.loginSection,
    eoiButtonSource,
  };

  useEffect(() => {
    const hasPasswordError = isPasswordValid(password);

    if (hasPasswordError) {
      setPasswordError(hasPasswordError);
    } else {
      setPasswordError('');
    }
  }, [password]);

  useEffect(() => {
    const hasEmailError = isEmailValid(learnerEmail);
    if (hasEmailError) {
      setEmailError(hasEmailError);
    } else {
      setEmailError('');
    }
  }, [learnerEmail]);

  useEffect(() => {
    if (phone.phoneNumber === '') {
      setHasPhoneError(true);
    } else {
      setHasPhoneError(false);
    }
  }, [phone]);

  /**
   * Handles API error responses during login, sets form errors, and announces them to screen readers.
   *
   * @param response - The error response from the login API
   */
  const handleError = (response: { responseJSON?: { errorCode?: string } }) => {
    trackSubmitEoi({ ...baseTrackingData, eoiStatus: 'api_failure' });

    resetBotManager();

    const { errorCode: code } = response?.responseJSON || {};

    if (code && HANDLED_ERRORS.includes(code)) {
      const errorMessages = errorMessageFactory();
      const errorCode = code as keyof typeof errorMessages;

      if (errorCode === 'invalidCredential') {
        setFormError(errorMessages[errorCode]());
        // Announce plain text version of the error for screen readers
        announce(_t("Sorry, we don't recognize that username or password. You can try again or reset your password."));
        // Delay focus to allow screen reader to announce the error first
        setTimeout(() => {
          verificationCodeInput.current?.focus();
        }, FOCUS_DELAY_AFTER_ANNOUNCEMENT_MS);
      } else {
        const errorMessage = errorMessages[errorCode];
        setFormError(errorMessage);
        // Announce the error if it's a string
        if (typeof errorMessage === 'string') {
          announce(errorMessage);
        }
      }

      setHas2FA(code ? NEEDS_2FA_ERRORS.includes(code) : false);
      setIsRunning(false);
    } else {
      const defaultError = getDefaultError();
      setFormError(defaultError);
      // Announce the default error if it's a string
      if (typeof defaultError === 'string') {
        announce(defaultError);
      }
    }
  };

  /**
   * Gets the region from the phone's country code, or returns the default region.
   */
  const getRegionFromPhone = () => {
    if (phone.countryCode) {
      const countryCode = newCountryCode(phone.countryCode);
      if (countryCode) {
        return toRegionFromCountryCode(countryCode) ?? DEFAULT_REGION;
      }
    }
    return DEFAULT_REGION;
  };

  /**
   * Checks if the phone number is invalid.
   * This checks both the hasPhoneError state (set by phone component callback)
   * and also validates directly for cases where the callback hasn't fired yet
   * (e.g., when focus moves directly to the submit button).
   * Uses isValidInternationalPhoneNumber for consistent validation with the phone component.
   */
  const isPhoneInvalid = () => {
    // Check the state first (set by phone component's handleError callback)
    if (hasPhoneError) return true;
    // Use the same validation as the phone component for consistency
    const region = getRegionFromPhone();
    return !isValidInternationalPhoneNumber(phone.phoneNumber as InternationalPhoneNumber, region, false);
  };

  /**
   * Collects all current validation errors and announces them to screen readers.
   * This function encapsulates the logic of gathering errors from various fields
   * and announcing them in a consistent format.
   *
   * @see WCAG 4.1.3 Status Messages
   */
  const collectAndAnnounceErrors = () => {
    const errors = [emailError, passwordError, isPhoneInvalid() ? _t('Invalid phone number') : null].filter(
      (e): e is string => Boolean(e)
    );
    if (errors.length > 0) {
      const errorList = errors.join('. ');
      announce(_t('Validation errors: #{errorList}', { errorList }));
    }
  };

  // Announce any existing validation errors when the Login button receives focus
  // This ensures users know about errors before pressing the button
  const handleLoginButtonFocus = () => {
    collectAndAnnounceErrors();
  };

  const handleLogin = async () => {
    // Exit early if a token retrieval is in progress to prevent multiple function calls.
    if (isRetrievingTokenRef?.current) return false;

    if (passwordError || emailError || hasPhoneError) {
      setHasFormError(true);
      collectAndAnnounceErrors();
      return;
    }

    let tokenData;
    try {
      tokenData = await getBotManagerTokenData();
    } catch {
      return setFormError(getDefaultError());
    }

    setIsRunning(true);

    const eventData = {
      namespace: {
        app: 'degree-description-app',
        page: 'login',
      },
    };
    trackAuthAttempts(eventData, 'login');

    const payload = {
      email: learnerEmail,
      password,
      ...tokenData,
      ...(verificationCode && { mfa: verificationCode }),
    };

    try {
      trackInteractEoi({ ...baseTrackingData, eoiInteraction: 'login' });

      await actions.login(payload);
      Instrumentation.login();

      trackSubmitEoi({ ...baseTrackingData, eoiStatus: 'success' });
      handleSubmit();

      if (navigateConfirmation) {
        // Add to implment the application url redirect after login success for Apply now button
        // The onLogin call needs to trigger the page refresh after the application url redirect
        navigateConfirmation();
        actions.onLogin();
      } else {
        // The onLogin call needs to trigger the page refresh before the query param update
        actions.onLogin();

        const actionFromQuery = simplifyQueryValue(location?.query?.action) ?? ''; // TODO handle null/undefined case
        const action: ActionQueryParam = Object.values(ACTION_QUERY_PARAMS).includes(actionFromQuery)
          ? actionFromQuery
          : ACTION_QUERY_PARAMS.EMAIL_ME_INFO;
        return setActionQueryParamAndReload(action, location, navigate, CONTINUE_EOI_PARAMS.CONTINUE_FROM_LOGIN);
      }
    } catch (e) {
      return handleError(e);
    }
  };

  const handleBack = () => {
    trackInteractEoi({ ...baseTrackingData, eoiInteraction: 'back' });
    handlePreviousSection();
  };

  const handleForgotPassword = () => {
    trackInteractEoi({ ...baseTrackingData, eoiInteraction: 'forgot_password' });
  };

  const { HeadingGroup, Content, Actions } = Dialog;

  return (
    <>
      <HeadingGroup
        supportText={_t(
          'After logging in, you will receive information on upcoming enrollment dates, webinars, and program news via email.'
        )}
      >
        {_t('Welcome back!')}
      </HeadingGroup>
      <Content>
        <>
          <div css={styles.reCAPTCHA} id={LOGIN_REDESIGN_RECAPTCHA_ID} data-testid="recaptcha-signup" />
          <EoiSSO trackingProductId={trackingProductId} shouldSetAutoFocus={shouldSetAutoFocus} />
          <Separator text={_t('or')} />
        </>

        {formError && <InlineNotification severity="error">{formError}</InlineNotification>}

        <TextField
          label={_t('Email')}
          type="email"
          placeholder={_t('name@email.com')}
          fullWidth
          onChange={(e) => handleEmailChange(e.target.value)}
          ref={verificationCodeInput}
          validationStatus={hasFormError && !!emailError ? 'error' : undefined}
          validationLabel={emailError}
          value={learnerEmail}
          autoComplete="email"
        />

        <div css={styles.passwordField}>
          <PasswordField
            label={_t('Password')}
            placeholder={_t('Enter your password')}
            fullWidth
            onChange={(e) => setPassword(e.target.value)}
            validationStatus={hasFormError && !!passwordError ? 'error' : undefined}
            validationLabel={passwordError}
            value={password}
          />
          <Link
            href="/reset"
            target="_blank"
            variant="quiet"
            typographyVariant="bodySecondary"
            onClick={handleForgotPassword}
          >
            {_t('Forgot password?')}
          </Link>
        </div>

        <InternationalPhoneNumberInput
          profile={{ phone }}
          isOptional={false}
          isEmptyValid={false}
          customStyles={customFormattedPhoneStyles}
          submissionError={hasFormError}
          handleFormChange={handlePhoneChange}
          handleError={(err) => {
            setHasPhoneError(err);
            if (err) {
              setHasFormError(true);
            }
          }}
          ref={phoneNumberRef}
          ariaDescribedBy="phone-consent-login"
        />

        <Typography2
          component="p"
          variant="bodySecondary"
          color="supportText"
          css={styles.supportText}
          id="phone-consent-login"
        >
          {disclaimerText}
        </Typography2>

        {has2FA && (
          <TextField
            label={_t('Verification code')}
            type="number"
            placeholder="000000"
            fullWidth
            onChange={(e) => setVerificationCode(e.target.value)}
            ref={verificationCodeInput}
            value={verificationCode}
            autoComplete="one-time-code"
          />
        )}
      </Content>
      <Actions>
        <Button
          onClick={handleLogin}
          onFocus={handleLoginButtonFocus}
          css={styles.primaryButton}
          loading={isRunning}
          disabled={isRunning}
        >
          {_t('Login and submit')}
        </Button>

        <Button onClick={handleBack} variant="secondary">
          {_t('Back')}
        </Button>
      </Actions>

      {/* Aria-live region for screen reader announcements of validation errors */}
      <VisuallyHidden>
        <div role="alert" aria-live="assertive" aria-atomic="true">
          {ariaAnnouncement}
        </div>
      </VisuallyHidden>
    </>
  );
};

export default LoginSection;
